import { marked } from 'marked';
import { addUserMessage, addBotMessage, scrollToBottom, createFeedbackWidget, extractTextFromBlocks, } from './chatMessage';
import { ApiService, AuthRequiredError, dispatchAuthRequired } from './apiService';
import { fetchEventSource } from '@microsoft/fetch-event-source';
// ── Constants ────────────────────────────────────────────────────────────────
const CHAT_INPUT_MAX_HEIGHT = 192; // px (≈ 12 rem)
const SOURCES_PANEL_MIN_WIDTH = 250; // px
const SOURCES_PANEL_MAX_WIDTH_RATIO = 0.8; // fraction of viewport width
const DEFAULT_LANGUAGE = 'English';
// ── Module-level state ───────────────────────────────────────────────────────
// NOTE: This file is loaded as an ES module via the webpack bundle, so it is
// implicitly deferred and all top-level DOM queries below are safe.
let apiService;
let conversationId = null;
let currentLanguage = localStorage.getItem('selectedLanguage') ?? DEFAULT_LANGUAGE;
window.currentLanguage = currentLanguage;
let activeStreamController = null;
// ── UI Elements ──────────────────────────────────────────────────────────────
const chatMessages = document.getElementById('chatMessages');
const chatInput = document.getElementById('chatInput');
const sendButton = document.getElementById('sendButton');
const toggleSources = document.getElementById('toggleSources');
const sourcesResizer = document.getElementById('sourcesResizer');
const sourcesPanel = document.getElementById('sourcesPanel');
const sourcesList = document.getElementById('sourcesList');
const closePanel = document.getElementById('closePanel');
const languageDropdown = document.querySelector('.dropdown-content');
const languageLoadingItem = document.getElementById('languageLoadingItem');
const newChatButton = document.getElementById('newChatButton');
const loginButton = document.getElementById('loginButton');
const loginOverlay = document.getElementById('loginOverlay');
const loginStatus = document.getElementById('loginStatus');
const openHistoryButton = document.getElementById('openHistoryButton');
const closeHistoryDrawerButton = document.getElementById('closeHistoryDrawer');
const historyDrawer = document.getElementById('historyDrawer');
const historyBackdrop = document.getElementById('historyBackdrop');
const historyList = document.getElementById('historyList');
const deleteAccountModal = document.getElementById('deleteAccountModal');
const deleteAccountAck = document.getElementById('deleteAccountAck');
const confirmDeleteAccountBtn = document.getElementById('confirmDeleteAccountBtn');
const deleteAccountResult = document.getElementById('deleteAccountResult');
// Validate required elements are found
const elementValidation = [
    [chatMessages, 'chatMessages'],
    [chatInput, 'chatInput'],
    [sendButton, 'sendButton'],
    [toggleSources, 'toggleSources'],
    [sourcesPanel, 'sourcesPanel'],
    [sourcesList, 'sourcesList'],
    [closePanel, 'closePanel'],
    [sourcesResizer, 'sourcesResizer'],
    [languageDropdown, 'languageDropdown'],
    [languageLoadingItem, 'languageLoadingItem'],
    [newChatButton, 'newChatButton'],
    [openHistoryButton, 'openHistoryButton'],
    [closeHistoryDrawerButton, 'closeHistoryDrawer'],
    [historyDrawer, 'historyDrawer'],
    [historyBackdrop, 'historyBackdrop'],
    [historyList, 'historyList'],
    [loginButton, 'loginButton'],
    [loginOverlay, 'loginOverlay'],
    [loginStatus, 'loginStatus'],
    [deleteAccountModal, 'deleteAccountModal'],
    [deleteAccountAck, 'deleteAccountAck'],
    [confirmDeleteAccountBtn, 'confirmDeleteAccountBtn'],
    [deleteAccountResult, 'deleteAccountResult'],
];
const missingElements = elementValidation.filter(([el]) => el === null).map(([, name]) => name);
if (missingElements.length > 0) {
    console.error('Error: Missing DOM elements:', missingElements);
}
// ── Sources panel helpers ────────────────────────────────────────────────────
function closeSources() {
    sourcesPanel.classList.remove('active');
    sourcesPanel.style.width = '';
    sourcesResizer.style.width = '';
    sourcesResizer.style.opacity = '0';
}
toggleSources.addEventListener('click', (e) => {
    e.preventDefault();
    if (sourcesPanel.classList.contains('active')) {
        closeSources();
    }
    else {
        sourcesResizer.style.width = '0.5rem';
        sourcesResizer.style.opacity = '1';
        sourcesPanel.classList.add('active');
    }
    const _reflow = sourcesPanel.offsetHeight; // force CSS reflow so transitions start correctly
});
closePanel.addEventListener('click', closeSources);
function renderSources(sources) {
    sourcesList.innerHTML = '';
    sources.forEach((source, index) => {
        const sourceItem = document.createElement('div');
        sourceItem.className = 'collapse collapse-plus bg-base-100 border border-base-300';
        sourceItem.id = `source-${index}`;
        const sourceRadio = document.createElement('input');
        sourceRadio.type = 'radio';
        sourceRadio.name = 'my-accordion';
        const accordionHeader = document.createElement('div');
        accordionHeader.className = 'collapse-title font-semibold';
        accordionHeader.innerHTML = marked.parse(`${source.url} (${source.score})`);
        const accordionContent = document.createElement('div');
        accordionContent.className = 'collapse-content text-sm';
        accordionContent.innerHTML = marked.parse(source.content);
        sourceItem.appendChild(sourceRadio);
        sourceItem.appendChild(accordionHeader);
        sourceItem.appendChild(accordionContent);
        sourcesList.appendChild(sourceItem);
    });
}
// ── Language loading ─────────────────────────────────────────────────────────
async function loadLanguages() {
    try {
        languageLoadingItem.textContent = 'Loading languages…';
        const product = window.__CONFIG__.product;
        const languages = await apiService.fetch(`api/languages?product=${encodeURIComponent(product)}`);
        if (!languageDropdown)
            return;
        languageDropdown.innerHTML = '';
        Object.entries(languages).forEach(([code, welcomeMessage]) => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.textContent = code.charAt(0).toUpperCase() + code.slice(1);
            a.addEventListener('click', () => {
                localStorage.setItem('selectedLanguage', code);
                currentLanguage = code;
                window.currentLanguage = code;
                const firstMessage = chatMessages.firstElementChild;
                if (firstMessage) {
                    addBotMessage(chatMessages, welcomeMessage, [], true, firstMessage);
                }
            });
            li.appendChild(a);
            languageDropdown.appendChild(li);
        });
    }
    catch (error) {
        console.error('Error loading languages:', error);
        if (languageDropdown) {
            languageDropdown.innerHTML = '<li class="text-error p-2">Failed to load languages</li>';
        }
    }
}
// ── Message sending ──────────────────────────────────────────────────────────
async function sendMessage() {
    const message = chatInput.value.trim();
    if (!message)
        return;
    if (activeStreamController) {
        activeStreamController.abort();
    }
    activeStreamController = new AbortController();
    // Remove feedback widget from previous assistant message
    const previousWidget = chatMessages.querySelector('.feedback-widget');
    if (previousWidget)
        previousWidget.remove();
    addUserMessage(chatMessages, message);
    chatInput.value = '';
    chatInput.style.height = 'auto';
    let currentResponse = '';
    addBotMessage(chatMessages, '', [], false);
    const botMessageElement = chatMessages.lastElementChild;
    botMessageElement.querySelector('.chat-bubble').innerHTML = `
        <div class="chat-content">
            <span class="loading loading-dots loading-sm"></span>
        </div>
    `;
    try {
        const product = window.__CONFIG__.product;
        let url = `${apiService.apiBaseUrl}/api/chat` +
            `?message=${encodeURIComponent(message)}` +
            `&product=${encodeURIComponent(product)}` +
            `&language=${encodeURIComponent(currentLanguage)}`;
        if (conversationId) {
            url += `&conversation_id=${encodeURIComponent(conversationId)}`;
        }
        const authHeaders = await apiService.getAuthHeaders();
        const headers = {
            ...authHeaders,
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
        };
        await fetchEventSource(url, {
            method: 'GET',
            headers,
            mode: 'cors',
            credentials: 'include',
            openWhenHidden: true,
            signal: activeStreamController.signal,
            onopen: async (response) => {
                if (response.ok && response.status === 200)
                    return;
                if (response.status === 401) {
                    dispatchAuthRequired('expired');
                }
                throw new Error(`Failed to establish connection: ${response.status} ${response.statusText}`);
            },
            onmessage: (event) => {
                if (event.event === 'message_delta') {
                    let data;
                    try {
                        data = JSON.parse(event.data);
                    }
                    catch (e) {
                        console.error('Failed to parse message_delta data:', e);
                        return;
                    }
                    currentResponse += data.delta;
                    addBotMessage(chatMessages, currentResponse, [], true, botMessageElement);
                }
                else if (event.event === 'message_complete') {
                    let data;
                    let response;
                    try {
                        data = JSON.parse(event.data);
                        response = JSON.parse(data.response);
                    }
                    catch (e) {
                        console.error('Failed to parse message_complete data:', e);
                        return;
                    }
                    addBotMessage(chatMessages, extractTextFromBlocks(response.blocks), response.sources ?? [], true, botMessageElement, data.span_id ?? null);
                    renderSources(response.sources ?? []);
                    if (data.conversation_id) {
                        conversationId = data.conversation_id;
                    }
                    if (data.span_id) {
                        const widget = createFeedbackWidget(data.span_id, apiService.apiBaseUrl ?? '');
                        botMessageElement.appendChild(widget);
                    }
                }
            },
            onerror: (error) => {
                console.error('Stream error:', error);
                throw error;
            },
        });
    }
    catch (error) {
        if (error?.name === 'AbortError')
            return;
        console.error('Error:', error);
        if (error instanceof AuthRequiredError) {
            dispatchAuthRequired(error.reason);
        }
        else {
            addBotMessage(chatMessages, 'Sorry, there was an error processing your request.');
        }
    }
}
// ── New chat ─────────────────────────────────────────────────────────────────
async function startNewChat() {
    conversationId = null;
    chatMessages.innerHTML = '';
    try {
        const product = window.__CONFIG__.product;
        const languages = await apiService.fetch(`api/languages?product=${encodeURIComponent(product)}`);
        const welcomeMessage = languages[currentLanguage];
        addBotMessage(chatMessages, welcomeMessage);
    }
    catch (error) {
        console.error('Error loading welcome message:', error);
        addBotMessage(chatMessages, 'Welcome! How can I help you today?');
    }
}
// ── Event listeners ──────────────────────────────────────────────────────────
sendButton.addEventListener('click', sendMessage);
newChatButton.addEventListener('click', startNewChat);
chatInput.addEventListener('input', () => {
    chatInput.style.height = 'auto';
    chatInput.style.height = `${Math.min(chatInput.scrollHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
});
chatInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
        if (e.shiftKey)
            return;
        e.preventDefault();
        void sendMessage();
    }
});
// ── Sources panel resizing ───────────────────────────────────────────────────
let isResizing = false;
sourcesResizer.addEventListener('mousedown', () => {
    if (!sourcesPanel.classList.contains('active'))
        return;
    isResizing = true;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopResizing);
});
function onMouseMove(e) {
    if (!isResizing)
        return;
    const newWidth = Math.max(SOURCES_PANEL_MIN_WIDTH, Math.min(window.innerWidth * SOURCES_PANEL_MAX_WIDTH_RATIO, window.innerWidth - e.clientX));
    if (sourcesPanel.classList.contains('active')) {
        sourcesPanel.style.width = `${newWidth}px`;
    }
}
function stopResizing() {
    isResizing = false;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', stopResizing);
    const currentWidth = sourcesPanel.style.width;
    sourcesPanel.style.setProperty('--custom-width', currentWidth);
    if (!sourcesPanel.classList.contains('active')) {
        sourcesPanel.style.width = '';
    }
}
// ── Authentication helpers ───────────────────────────────────────────────────
function setApplicationInert(inert) {
    for (const element of document.querySelectorAll('header, .app-container, .input-container')) {
        element.inert = inert;
        element.setAttribute('aria-hidden', String(inert));
    }
}
function setStatus(msg) {
    loginStatus.textContent = msg ?? '';
    loginStatus.classList.toggle('hidden', !msg);
}
function showLoginGate(message = null, isError = false) {
    setStatus(message);
    loginStatus.classList.toggle('text-error', isError);
    loginButton.classList.remove('hidden');
    loginButton.disabled = false;
    loginOverlay.classList.remove('hidden');
    setApplicationInert(true);
}
// Survives the redirect to Entra and back: sessionStorage is per-tab and per-origin, and MSAL's logout clears only its
// own prefixed keys. This avoids a dedicated post-logout URL, which would have to be registered as a redirect URI on
// every one of the eight origins the app is served from.
const ACCOUNT_DELETED_FLAG = 'pace.accountDeleted';
function markAccountDeleted() {
    try {
        sessionStorage.setItem(ACCOUNT_DELETED_FLAG, '1');
    }
    catch {
        // Private mode or storage disabled: the user simply gets the neutral gate instead of the confirmation.
    }
}
/** Read and clear the flag, so the confirmation is shown exactly once. */
function consumeAccountDeletedFlag() {
    try {
        if (sessionStorage.getItem(ACCOUNT_DELETED_FLAG) === null)
            return false;
        sessionStorage.removeItem(ACCOUNT_DELETED_FLAG);
        return true;
    }
    catch {
        return false;
    }
}
function hideLoginGate() {
    loginOverlay.classList.add('hidden');
    setApplicationInert(false);
}
// ── htmx authentication wiring ───────────────────────────────────────────────
// htmx's `htmx:confirm` event is the documented escape hatch for async
// pre-request logic: calling `event.preventDefault()` pauses the request, and
// `evt.detail.issueRequest(true)` resumes it once we're ready. We use this to
// acquire a fresh MSAL bearer token (silently renewed by ApiService) right
// before every htmx request instead of freezing one at startup.
let pendingAuthHeaders = {};
document.body.addEventListener('htmx:confirm', ((event) => {
    const evt = event;
    event.preventDefault();
    void apiService
        .getAuthHeaders()
        .then((headers) => {
        pendingAuthHeaders = headers;
    })
        .catch((error) => {
        console.error('Failed to acquire auth headers for htmx request:', error);
        pendingAuthHeaders = {};
    })
        .finally(() => {
        evt.detail.issueRequest(true);
    });
}));
document.body.addEventListener('htmx:configRequest', ((event) => {
    const evt = event;
    Object.assign(evt.detail.headers, pendingAuthHeaders);
}));
document.body.addEventListener('htmx:responseError', ((event) => {
    const evt = event;
    if (evt.detail.xhr.status === 401) {
        dispatchAuthRequired('expired');
        return;
    }
    if (evt.detail.target === deleteAccountResult) {
        deleteAccountResult.innerHTML =
            '<span class="text-error">Failed to delete your account. Please try again.</span>';
        // Re-enable so the user can retry; the click handler disabled them on submit.
        deleteAccountAck.disabled = false;
        confirmDeleteAccountBtn.disabled = !deleteAccountAck.checked;
    }
}));
// ── Initialisation ───────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
    void (async () => {
        // Consumed before anything can throw, so the confirmation is never lost to an unrelated init failure.
        const justDeleted = consumeAccountDeletedFlag();
        try {
            setApplicationInert(true);
            apiService = new ApiService(window.__CONFIG__.apiBaseUrl, window.__CONFIG__.auth);
            await apiService.initialize();
            if (!(await apiService.isAuthenticated())) {
                showLoginGate(justDeleted
                    ? 'Your account and all of your conversations have been deleted. You are signed out.'
                    : null);
                return;
            }
            hideLoginGate();
            document.body.dispatchEvent(new CustomEvent('legal:check'));
            await loadLanguages();
            await startNewChat();
        }
        catch (error) {
            console.error('Failed to initialize application:', error);
            const message = error instanceof Error ? error.message : 'Authentication could not be initialized.';
            showLoginGate(message, true);
        }
    })();
});
async function handleLogin() {
    try {
        loginButton.disabled = true;
        setStatus('Redirecting to sign in…');
        await apiService.login();
    }
    catch (error) {
        console.error('Login failed:', error);
        showLoginGate('Sign-in could not be started. Please try again.', true);
    }
}
loginButton.addEventListener('click', () => void handleLogin());
window.handleLogout = async function () {
    try {
        await apiService.logout();
    }
    catch (error) {
        console.error('Logout failed:', error);
        showLoginGate('Sign-out could not be completed. Please try again.', true);
    }
};
window.openDocumentation = async function () {
    try {
        const product = encodeURIComponent(window.__CONFIG__.product);
        const response = await apiService.fetch(`api/online-help?product=${product}`);
        const url = await response.text();
        window.open(url, '_blank');
    }
    catch (error) {
        console.error('Failed to open documentation:', error);
    }
};
window.openTermsOfUseModal = function (event) {
    event.preventDefault();
    document.getElementById('termsOfUseModal')?.showModal();
    // The modal body is fetched on demand rather than on `intersect`: DaisyUI hides a closed `.modal` with
    // visibility/opacity, not `display: none`, so it keeps a layout box and IntersectionObserver fires it at page load
    // - before anyone has signed in.
    document.body.dispatchEvent(new CustomEvent('terms:open'));
};
// Only an actual expiry gets the "sign in again" wording. A first visit, a cleared cache or a private window raises the
// same gate with no status text, because nothing expired there.
window.addEventListener('auth:required', (event) => {
    const { reason } = event.detail ?? {};
    showLoginGate(reason === 'expired' ? 'Your session has expired. Sign in again to continue.' : null);
});
// ── Conversation History ─────────────────────────────────────────────────────
function openHistoryDrawer() {
    historyDrawer.classList.add('open');
    historyBackdrop.classList.remove('hidden');
    void loadConversationHistory();
}
function closeHistoryDrawer() {
    historyDrawer.classList.remove('open');
    historyBackdrop.classList.add('hidden');
}
async function loadConversationHistory() {
    historyList.innerHTML = '<div class="p-4 text-sm text-base-content/60 text-center">Loading…</div>';
    try {
        const conversations = await apiService.fetch('api/conversations');
        renderConversationList(conversations);
    }
    catch (error) {
        console.error('Failed to load conversations:', error);
        historyList.innerHTML = '<div class="p-4 text-sm text-error">Failed to load conversations.</div>';
    }
}
function formatConversationDate(dateStr) {
    if (!dateStr)
        return '';
    // If no timezone indicator is present the timestamp is naive UTC — append Z
    const normalized = /Z|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z';
    const date = new Date(normalized);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} - ${hh}:${min}`;
}
function getConversationTitle(conversation) {
    if (conversation.title)
        return conversation.title;
    const firstUserMsg = conversation.messages?.find((m) => m.role === 'user');
    const text = extractTextFromBlocks(firstUserMsg?.blocks);
    if (text)
        return text.length > 60 ? text.slice(0, 60) + '…' : text;
    return 'Untitled Conversation';
}
function renderConversationList(conversations) {
    if (!conversations?.length) {
        historyList.innerHTML =
            '<div class="p-4 text-sm text-base-content/60 text-center">No past conversations found.</div>';
        return;
    }
    conversations.sort((a, b) => {
        const aDate = a.updated_at ?? a.created_at;
        const bDate = b.updated_at ?? b.created_at;
        if (!aDate)
            return 1;
        if (!bDate)
            return -1;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
    historyList.innerHTML = '';
    for (const conv of conversations) {
        const title = getConversationTitle(conv);
        const dateLabel = formatConversationDate(conv.updated_at ?? conv.created_at);
        const row = document.createElement('div');
        row.className = 'flex items-center group';
        row.id = `conv-${conv.id}`;
        const item = document.createElement('button');
        item.className = 'history-item flex-1 text-left min-w-0';
        item.innerHTML = `
            <div class="text-sm font-medium truncate">${title}</div>
            ${dateLabel ? `<div class="text-xs text-base-content/50 mt-0.5">${dateLabel}</div>` : ''}
        `;
        item.addEventListener('click', () => void loadConversation(conv));
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'btn btn-ghost btn-xs text-error opacity-0 group-hover:opacity-100 shrink-0 mr-2';
        deleteBtn.title = 'Delete conversation';
        deleteBtn.dataset.convTitle = title;
        deleteBtn.innerHTML = `
            <svg class="h-4 w-4" fill="none" stroke="currentColor">
                <use href="#trash-icon"></use>
            </svg>
        `;
        deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            window.requestDeleteConversation(conv.id, title);
        });
        row.appendChild(item);
        row.appendChild(deleteBtn);
        historyList.appendChild(row);
    }
}
async function loadConversation(conversation) {
    closeHistoryDrawer();
    try {
        const fullConversation = await apiService.fetch(`api/conversation/${conversation.id}`);
        conversationId = fullConversation.id;
        chatMessages.innerHTML = '';
        for (const message of fullConversation.messages ?? []) {
            if (message.role === 'user') {
                addUserMessage(chatMessages, extractTextFromBlocks(message.blocks));
            }
            else if (message.role === 'assistant') {
                addBotMessage(chatMessages, extractTextFromBlocks(message.blocks), message.sources ?? [], false, null);
            }
        }
        const assistantMessages = fullConversation.messages?.filter((m) => m.role === 'assistant') ?? [];
        const lastAssistantMsg = assistantMessages[assistantMessages.length - 1];
        if (lastAssistantMsg?.sources?.length) {
            renderSources(lastAssistantMsg.sources);
        }
        scrollToBottom(chatMessages);
    }
    catch (error) {
        console.error('Failed to load conversation:', error);
    }
}
openHistoryButton.addEventListener('click', openHistoryDrawer);
closeHistoryDrawerButton.addEventListener('click', closeHistoryDrawer);
historyBackdrop.addEventListener('click', closeHistoryDrawer);
// ── Conversation Deletion ────────────────────────────────────────────────────
let _pendingDeleteConvId = null;
window.requestDeleteConversation = function (convId, title) {
    _pendingDeleteConvId = convId;
    const titleEl = document.getElementById('deleteConvTitle');
    if (titleEl)
        titleEl.textContent = `"${title}"`;
    document.getElementById('deleteConfirmModal')?.showModal();
};
window.confirmConversationDelete = async function () {
    document.getElementById('deleteConfirmModal')?.close();
    if (!_pendingDeleteConvId)
        return;
    const convId = _pendingDeleteConvId;
    _pendingDeleteConvId = null;
    try {
        await apiService.fetch(`api/conversation/${convId}`, { method: 'DELETE' });
        const row = document.getElementById(`conv-${convId}`);
        if (row)
            row.remove();
        if (conversationId === convId) {
            await startNewChat();
        }
    }
    catch (error) {
        console.error('Failed to delete conversation:', error);
    }
};
// ── Account Deletion ─────────────────────────────────────────────────────────
window.openDeleteAccountModal = function () {
    deleteAccountAck.checked = false;
    confirmDeleteAccountBtn.disabled = true;
    deleteAccountResult.innerHTML = '';
    deleteAccountModal.showModal();
};
deleteAccountAck.addEventListener('change', () => {
    confirmDeleteAccountBtn.disabled = !deleteAccountAck.checked;
});
// Guard against double-submission: htmx leaves the button interactive while the request is in flight, and a second
// DELETE would hit an account whose directory object is already gone.
confirmDeleteAccountBtn.addEventListener('click', () => {
    confirmDeleteAccountBtn.disabled = true;
    deleteAccountAck.disabled = true;
});
document.body.addEventListener('account-deleted', () => {
    // The server confirmed the purge (HX-Trigger: account-deleted); clear all client-side state
    // and sign the user out so a returning session starts from a clean slate.
    void (async () => {
        conversationId = null;
        chatMessages.innerHTML = '';
        historyList.innerHTML = '';
        sourcesList.innerHTML = '';
        closeSources();
        closeHistoryDrawer();
        markAccountDeleted();
        setTimeout(() => {
            // Default post-logout URI (the app origin). The flag set above turns the login gate we land on into a
            // deletion confirmation, so no separate page - and no extra registered redirect URI - is needed.
            void apiService.logout().catch((error) => {
                console.error('Logout after account deletion failed:', error);
            });
        }, 1500);
    })();
});
